/**
 * /settings/imports (v11, admin-style) — Importações fiel ao TiFlux: select
 * "Importar" (Clientes | Recursos), botões "Planilha de exemplo" (download do
 * CSV) e "Anexar arquivo" (upload + processamento síncrono com spinner) e
 * tabela "Histórico de importações" (Nome | Importado por | Importação de |
 * Status | Data de importação) com dialog de erros por linha quando houver.
 */
import { useRef, useState } from 'react';
import { Link } from '@tanstack/react-router';
import { queryOptions, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { FileText, ShieldAlert, Upload } from 'lucide-react';
import {
  IMPORT_JOB_STATUS_LABELS,
  IMPORT_KIND_LABELS,
  IMPORT_MAX_ROWS,
  type ImportJob,
  type ImportKind,
} from '@erp/shared';
import { useAuthUser } from '@/auth/use-auth';
import { apiDownload, apiList, authHeaders, type ApiError } from '@/lib/api-client';
import { env } from '@/config/env';
import { Button } from '@/components/ui/button';
import { Label, Select } from '@/components/ui/input';
import { Badge, Dialog, EmptyState, Skeleton, Spinner } from '@/components/ui/misc';
import { formatDateTime } from '@/lib/utils';

const IMPORTS_KEY = ['erp', 'imports'] as const;

export function importsHistoryQuery() {
  return queryOptions({
    queryKey: [...IMPORTS_KEY, 'list'] as const,
    queryFn: async ({ signal }) => {
      const res = await apiList<ImportJob>('/imports', { signal });
      if (res.error) throw res.error;
      return res.data.items;
    },
  });
}

// Códigos das rotas /imports (invalid_multipart/invalid_kind/file_required
// cobrem mau uso da UI; os dois últimos são os esperáveis pelo usuário).
const ERROR_LABELS: Record<string, string> = {
  validation_error: 'Arquivo inválido — confira o formato da planilha de exemplo.',
  invalid_multipart: 'Arquivo inválido — confira o formato da planilha de exemplo.',
  invalid_kind: 'Selecione o tipo de importação.',
  file_required: 'Selecione um arquivo CSV.',
  file_too_large: 'O arquivo excede o limite aceito.',
  too_many_rows: `A planilha excede o limite de ${IMPORT_MAX_ROWS} linhas.`,
};

function errorMessage(e: unknown): string {
  const err = e as Partial<ApiError> | null;
  const label = err?.error ? ERROR_LABELS[err.error] : undefined;
  if (label) return label;
  if (err?.status === 422) return 'Arquivo inválido — confira o formato da planilha de exemplo.';
  if (err?.status === 0) return 'Sem conexão com o servidor.';
  return err?.error ?? 'Erro inesperado.';
}

/**
 * Upload multipart do import: além do campo 'file', o POST /imports leva o
 * campo 'kind' — o helper apiUpload só manda o arquivo, então o fetch é local
 * (mesmo padrão: bearer manual, lança ApiError-like em falha).
 */
async function uploadImport(kind: ImportKind, file: File): Promise<ImportJob> {
  const form = new FormData();
  form.append('file', file);
  form.append('kind', kind);
  const headers: Record<string, string> = authHeaders();
  const url = new URL(`${env.apiBaseUrl}/api/v1/imports`, window.location.origin);
  const res = await fetch(url, { method: 'POST', headers, body: form });
  if (!res.ok) {
    let body: { error?: string } = {};
    try {
      body = (await res.json()) as { error?: string };
    } catch {
      /* corpo de erro não-JSON */
    }
    throw { status: res.status, error: body.error ?? `http_${res.status}` };
  }
  return (await res.json()) as ImportJob;
}

export function ImportsPage() {
  const user = useAuthUser();
  if (user?.role !== 'admin') {
    return (
      <div className="flex h-64 flex-col items-center justify-center gap-2 rounded-lg border bg-card text-center shadow-xs">
        <ShieldAlert className="size-8 text-muted-foreground" />
        <p className="text-sm font-medium">Acesso restrito</p>
        <p className="max-w-sm text-sm text-muted-foreground">
          As importações de dados são feitas por administradores da conta.
        </p>
      </div>
    );
  }
  return <ImportsAdmin />;
}

function ImportsAdmin() {
  const qc = useQueryClient();
  const historyQ = useQuery(importsHistoryQuery());
  const [kind, setKind] = useState<ImportKind | ''>('');
  const [error, setError] = useState<string | null>(null);
  const [errorsDialog, setErrorsDialog] = useState<ImportJob | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const upload = useMutation({
    mutationFn: ({ k, file }: { k: ImportKind; file: File }) => uploadImport(k, file),
    onSuccess: (job) => {
      void qc.invalidateQueries({ queryKey: IMPORTS_KEY });
      // Novos registros entram direto nas listas correspondentes.
      void qc.invalidateQueries({ queryKey: ['erp', 'clients'] });
      void qc.invalidateQueries({ queryKey: ['erp', 'resources'] });
      if (job.errors.length > 0) setErrorsDialog(job);
    },
    onError: (e) => setError(errorMessage(e)),
  });

  const sample = useMutation({
    mutationFn: (k: ImportKind) =>
      // Mesmos nomes que o Content-Disposition do servidor.
      apiDownload(
        `/imports/sample?kind=${k}`,
        k === 'clients' ? 'exemplo-clientes.csv' : 'exemplo-recursos.csv'
      ),
    onError: () => setError('Falha ao baixar a planilha de exemplo.'),
  });

  const pick = (file: File | undefined) => {
    if (!file || kind === '') return;
    setError(null);
    upload.mutate({ k: kind, file });
  };

  const history = historyQ.data ?? [];

  return (
    <div className="rounded-lg border bg-card p-4 shadow-xs sm:p-5">
      <nav className="text-[13px] text-muted-foreground" aria-label="Breadcrumb">
        <Link to="/settings" className="transition-colors hover:text-foreground">
          Configurações
        </Link>
        <span className="mx-1.5">/</span>
        <span className="font-medium text-foreground">Importações</span>
      </nav>
      <h1 className="mt-2 text-xl leading-7 font-semibold">Importações</h1>

      {/* ── Importar: tipo + planilha de exemplo + anexar ─────────────────── */}
      <div className="mt-4 max-w-xl space-y-1.5">
        <Label htmlFor="import-kind">
          Importar <span className="text-destructive">*</span>
        </Label>
        <div className="flex flex-wrap items-center gap-2">
          <Select
            id="import-kind"
            value={kind}
            onChange={(e) => setKind(e.target.value as ImportKind | '')}
            className="w-56"
          >
            <option value="">Selecione</option>
            <option value="clients">Clientes</option>
            <option value="resources">Recursos</option>
          </Select>
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="h-9"
            disabled={kind === '' || sample.isPending}
            onClick={() => kind !== '' && sample.mutate(kind)}
          >
            {sample.isPending ? <Spinner className="size-4" /> : <FileText />}
            Planilha de exemplo
          </Button>
          <input
            ref={inputRef}
            type="file"
            accept=".csv,text/csv"
            className="hidden"
            onChange={(e) => {
              pick(e.target.files?.[0]);
              e.target.value = '';
            }}
          />
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="h-9"
            disabled={kind === '' || upload.isPending}
            onClick={() => inputRef.current?.click()}
          >
            {upload.isPending ? <Spinner className="size-4" /> : <Upload />}
            {upload.isPending ? 'Importando…' : 'Anexar arquivo'}
          </Button>
        </div>
        <p className="text-xs text-muted-foreground">
          CSV com as colunas da planilha de exemplo, até {IMPORT_MAX_ROWS} linhas — linhas
          inválidas são ignoradas e listadas no histórico.
        </p>
        {error ? (
          <p className="text-sm text-destructive" role="alert">
            {error}
          </p>
        ) : null}
      </div>

      {/* ── Histórico ─────────────────────────────────────────────────────── */}
      <h2 className="mt-6 text-base font-semibold">Histórico de importações</h2>
      <div className="mt-3">
        {historyQ.isPending ? (
          <div className="space-y-2" aria-busy>
            {Array.from({ length: 3 }).map((_, i) => (
              <Skeleton key={i} className="h-10 w-full" />
            ))}
          </div>
        ) : historyQ.isError ? (
          <EmptyState
            message={`Erro ao carregar o histórico: ${errorMessage(historyQ.error)}`}
          />
        ) : (
          <div className="overflow-x-auto rounded-md border">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-muted text-left text-[13px]">
                  <th className="h-10 px-4 font-semibold">Nome</th>
                  <th className="h-10 px-4 font-semibold">Importado por</th>
                  <th className="h-10 px-4 font-semibold">Importação de</th>
                  <th className="h-10 px-4 font-semibold">Status</th>
                  <th className="h-10 px-4 font-semibold">Data de importação</th>
                </tr>
              </thead>
              <tbody>
                {history.length === 0 ? (
                  <tr>
                    <td colSpan={5}>
                      <EmptyState className="h-40" message="Não há dados" />
                    </td>
                  </tr>
                ) : (
                  history.map((job) => (
                    <tr
                      key={job.id}
                      className="h-11 border-b border-divider transition-colors last:border-0 hover:bg-accent/60"
                    >
                      <td className="max-w-72 truncate px-4 font-medium" title={job.filename}>
                        {job.filename}
                      </td>
                      <td className="max-w-52 truncate px-4">{job.createdByName ?? '—'}</td>
                      <td className="px-4 whitespace-nowrap">{IMPORT_KIND_LABELS[job.kind]}</td>
                      <td className="px-4">
                        <span className="flex flex-wrap items-center gap-1.5">
                          <Badge variant={job.status === 'done' ? 'success' : 'destructive'}>
                            {IMPORT_JOB_STATUS_LABELS[job.status]}
                          </Badge>
                          <span className="text-xs text-muted-foreground tabular-nums">
                            {job.importedRows}/{job.totalRows}
                          </span>
                          {job.errors.length > 0 ? (
                            <button
                              type="button"
                              className="text-xs font-medium text-destructive hover:underline"
                              onClick={() => setErrorsDialog(job)}
                            >
                              {job.errors.length}{' '}
                              {job.errors.length === 1 ? 'erro' : 'erros'}
                            </button>
                          ) : null}
                        </span>
                      </td>
                      <td className="px-4 text-muted-foreground tabular-nums whitespace-nowrap">
                        {formatDateTime(job.createdAt)}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {errorsDialog ? (
        <ImportErrorsDialog job={errorsDialog} onClose={() => setErrorsDialog(null)} />
      ) : null}
    </div>
  );
}

/** Erros por linha do job ({row, message}, cap 100 no servidor). */
function ImportErrorsDialog({ job, onClose }: { job: ImportJob; onClose: () => void }) {
  return (
    <Dialog open onClose={onClose} title={`Erros da importação — ${job.filename}`}>
      <div className="space-y-3">
        <p className="text-sm text-muted-foreground">
          {job.importedRows} de {job.totalRows}{' '}
          {job.totalRows === 1 ? 'linha importada' : 'linhas importadas'}. As linhas abaixo
          não foram importadas:
        </p>
        <ul className="max-h-80 divide-y divide-divider overflow-y-auto rounded-md border border-divider">
          {job.errors.map((err, i) => (
            <li key={i} className="flex items-baseline gap-3 px-3 py-2 text-sm">
              <span className="shrink-0 font-mono text-xs text-muted-foreground tabular-nums">
                Linha {err.row}
              </span>
              <span className="min-w-0">{err.message}</span>
            </li>
          ))}
        </ul>
        <div className="flex justify-end">
          <Button type="button" variant="outline" size="sm" onClick={onClose}>
            Fechar
          </Button>
        </div>
      </div>
    </Dialog>
  );
}
